import {
    BulkRequestFilterBar,
    BulkRequestFilterContextProvider,
} from "./BulkRequestBar/BulkRequestFilterBar/BulkRequestFilterBar";
import { BulkRequestInProgress } from "./BulkRequestInProgress";
import { BulkRequestSubmitButton } from "./BulkRequestBar/BulkRequestSubmit/BulkRequestSubmitButton";
import { BulkRequestTable } from "./BulkRequestTable/BulkRequestTable";
import { type FrozenSetDispatch, useFrozenState } from "utils/useFrozenState";
import { type StrictlyFrozenSet, setMinus, setUnion, toReadonlySet, updateSeenRows, useStableSet } from "utils/sets";
import { selectAll } from "./bulkRequestUtils";
import { useBulkRequestApplyFilter } from "./useBulkRequestApplyFilter";
import { useBulkRequestFilterContext } from "./BulkRequestBar/BulkRequestFilterBar/bulkRequestFilterContext";
import { useBulkRequestSubmission } from "./useBulkRequestSubmission";
import { useChangeOverlay } from "./useChangeOverlay";
import { useEffect, useState } from "react";
import { useQueryWithInvalidate } from "./useQueryWithInvalidate";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";

const noIds = toReadonlySet<string>([]);

type Strings = StrictlyFrozenSet<string>;
type WriteStrings = FrozenSetDispatch<string>;

/**
 * Query data from the database and automatically select new rows.
 * @param enabled Determines whether or not the query is enabled.
 * @param setSelectedRows Callback to change row selection.
 * @returns React Query properties.
 */
function useQueryWithSelection(enabled: boolean, setSelectedRows: WriteStrings) {
    // Track which rows we've seen, so we can auto-select new rows
    // This particular set is constructed once, and then mutated in-place
    const [seenRows] = useState(() => new Set<string>());

    // Retrieve data from the back-end
    // This serves as the baseline data
    const result = useQueryWithInvalidate(enabled);

    const rows = result.data?.rows;

    useEffect(() => {
        if (!rows) {
            return;
        }

        const allDataRows = selectAll(rows);

        // Track all new rows we've seen
        // Add eligible new rows to the selection
        const newRows = updateSeenRows(allDataRows, seenRows);
        setSelectedRows((x) => setUnion(x, newRows));
    }, [rows, seenRows, setSelectedRows]);

    return result;
}

function useSelectableSet(setSelectedRows: WriteStrings, withoutCommSet: Strings, combinedData: BulkRequestRecord[]) {
    useEffect(() => {
        setSelectedRows((x) => setMinus(x, withoutCommSet));
    }, [setSelectedRows, withoutCommSet]);

    const selectableSet = useStableSet(
        setMinus(
            combinedData.map((x) => x.pmsId),
            withoutCommSet,
        ),
    );

    return selectableSet;
}

interface BulkRequestCoreProps {
    readonly enabled: boolean;
    readonly onSubmitted?: () => void;
    readonly mutate: (rows: BulkRequestRecord[]) => Promise<void>;
    readonly isSubmitting: boolean;
}

const BulkRequestCore = ({ enabled, onSubmitted, mutate, isSubmitting }: BulkRequestCoreProps) => {
    const [selectedRows, setSelectedRows] = useFrozenState(noIds);

    // Retrieve data from the back-end
    const { isPending, data } = useQueryWithSelection(enabled, setSelectedRows);

    // Filter the data, as prescribed by the user
    const { filter } = useBulkRequestFilterContext();
    const filteredData = useBulkRequestApplyFilter(data?.rows, filter);

    // Store changes made by the user in an overlay
    const [combinedData, handleSave, withoutCommSet] = useChangeOverlay(filteredData);

    // Deselect patients without communication
    const selectableSet = useSelectableSet(setSelectedRows, withoutCommSet, combinedData);

    return (
        <>
            <div className="vyne-tabs-content-header">
                <BulkRequestFilterBar />
                <BulkRequestSubmitButton
                    combinedData={combinedData}
                    selectedRows={selectedRows}
                    mutate={mutate}
                    isSubmitting={isSubmitting}
                />
            </div>
            <div className="vyne-tabs-content">
                {data?.busy ? (
                    <BulkRequestInProgress onSubmitted={onSubmitted} />
                ) : (
                    <BulkRequestTable
                        isPending={isPending}
                        combinedData={combinedData}
                        handleSave={handleSave}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        selectableSet={selectableSet}
                    />
                )}
            </div>
        </>
    );
};

interface BulkRequestProps {
    /** Conveys that the tab hosting Bulk Request is active. */
    readonly enabled: boolean;

    /** Callback that's invoked upon a Bulk Request submission. */
    readonly onSubmitted?: () => void;

    /** The index of the aging bucket to filter by. */
    readonly agingIndex: number | null;
}

/** Main UI for performing bulk payment requests using PMS data. */
export const BulkRequest = ({ enabled, onSubmitted, agingIndex }: BulkRequestProps) => {
    // Submit bulk request to API
    const { isSubmitting, mutate, draftId } = useBulkRequestSubmission(onSubmitted);

    return (
        <BulkRequestFilterContextProvider agingIndex={agingIndex}>
            <BulkRequestCore
                key={draftId}
                enabled={enabled}
                onSubmitted={onSubmitted}
                isSubmitting={isSubmitting}
                mutate={mutate}
            />
        </BulkRequestFilterContextProvider>
    );
};
